import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Users, LogIn, LogOut, Download, Search, Eye, MapPin, User, MessageSquare, Flag, ShieldOff } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useAuth } from '../../../contexts/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../../../services/api';

const API = `${API_BASE_URL}/api/admin/security`;

const StatCard = ({ icon: Icon, label, value, tone = 'primary' }) => (
  <Card data-testid={`stat-${label}`}>
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center bg-${tone}/10`}>
        <Icon className={`h-5 w-5 text-${tone}`} />
      </div>
      <div>
        <div className="text-2xl font-bold">{value ?? '—'}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </CardContent>
  </Card>
);

const SecurityMonitor = () => {
  const { token } = useAuth();
  const [overview, setOverview] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterKind, setFilterKind] = useState('all');
  const [searchIp, setSearchIp] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [geoResults, setGeoResults] = useState({});
  const [traceQuery, setTraceQuery] = useState('');
  const [traceResult, setTraceResult] = useState(null);
  const [tracing, setTracing] = useState(false);

  const loadOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/overview`, { headers: { Authorization: `Bearer ${token}` } });
      setOverview(res.data);
    } catch (e) {
      console.error('Erreur chargement overview sécurité', e);
    }
  }, [token]);

  const loadConnections = useCallback(async () => {
    setLoading(true);
    try {
      const params = { kind: filterKind === 'all' ? undefined : filterKind };
      if (searchIp) params.ip = searchIp;
      if (searchEmail) params.email = searchEmail;
      const res = await axios.get(`${API}/connections`, { headers: { Authorization: `Bearer ${token}` }, params });
      setConnections(res.data.items);
    } catch (e) {
      console.error('Erreur chargement connexions', e);
    } finally {
      setLoading(false);
    }
  }, [token, filterKind, searchIp, searchEmail]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadConnections(); }, [loadConnections]);

  const handleExport = () => {
    window.open(`${API}/export?kind=${filterKind}`, '_blank');
  };

  const handleGeolocate = async (ip) => {
    if (!ip || geoResults[ip]) return;
    try {
      const res = await axios.get(`${API}/geolocate`, { headers: { Authorization: `Bearer ${token}` }, params: { ip } });
      setGeoResults((prev) => ({ ...prev, [ip]: res.data }));
    } catch (e) {
      setGeoResults((prev) => ({ ...prev, [ip]: { error: 'Erreur de géolocalisation' } }));
    }
  };

  const handleTrace = async (e) => {
    e.preventDefault();
    if (!traceQuery.trim()) return;
    setTracing(true);
    setTraceResult(null);
    try {
      const res = await axios.get(`${API}/trace`, { headers: { Authorization: `Bearer ${token}` }, params: { query: traceQuery.trim() } });
      setTraceResult(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors du traçage');
    } finally {
      setTracing(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('fr-FR') : '—';

  return (
    <div className="min-h-screen" data-testid="security-monitor">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-end mb-6">
          <Button variant="outline" size="sm" onClick={handleExport} className="flex items-center gap-2" data-testid="btn-export-csv">
            <Download className="h-4 w-4" /> Exporter en CSV
          </Button>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <Shield className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Sécurité & Connexions</h1>
        </div>

        {/* Compteurs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Eye} label="Visites (total)" value={overview?.total_visits} />
          <StatCard icon={Eye} label="Visites aujourd'hui" value={overview?.visits_today} />
          <StatCard icon={Users} label="IP uniques aujourd'hui" value={overview?.unique_ips_today} />
          <StatCard icon={LogIn} label="Connexions réussies (total)" value={overview?.total_logins_success} tone="secondary" />
          <StatCard icon={LogOut} label="Connexions échouées (total)" value={overview?.total_logins_failed} tone="destructive" />
          <StatCard icon={LogOut} label="Échecs aujourd'hui" value={overview?.failed_logins_today} tone="destructive" />
          <StatCard icon={LogIn} label="Réussites aujourd'hui" value={overview?.successful_logins_today} tone="secondary" />
          <StatCard icon={Eye} label="Visites cette semaine" value={overview?.visits_this_week} />
        </div>

        {/* Traçage complet par email ou IP */}
        <Card className="mb-8" data-testid="trace-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <User className="h-5 w-5 text-primary" />
              <h2 className="font-semibold">Tracer une personne (email ou IP)</h2>
            </div>
            <form onSubmit={handleTrace} className="flex gap-2 mb-4">
              <Input
                placeholder="Email de l'adhérent/donateur/invité, ou adresse IP"
                value={traceQuery}
                onChange={(e) => setTraceQuery(e.target.value)}
                className="flex-1"
                data-testid="trace-input"
              />
              <Button type="submit" disabled={tracing} data-testid="trace-submit">{tracing ? 'Recherche...' : 'Tracer'}</Button>
            </form>

            {traceResult && (
              <div className="space-y-4 text-sm" data-testid="trace-result">
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2"><LogIn className="h-4 w-4" /> Connexions ({traceResult.login_events?.length || 0})</h3>
                  {traceResult.login_events?.length > 0 ? (
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {traceResult.login_events.map((l, i) => (
                        <div key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                          <span className={l.success ? 'text-secondary' : 'text-destructive'}>{l.success ? '✓' : '✗'}</span>
                          {l.email} — {l.ip} — {formatDate(l.created_at)}
                          {l.ip && (
                            <button onClick={() => handleGeolocate(l.ip)} className="text-muted-foreground hover:text-primary">
                              <MapPin className="h-3 w-3" />
                            </button>
                          )}
                          {geoResults[l.ip] && !geoResults[l.ip].error && (
                            <span>({geoResults[l.ip].city}, {geoResults[l.ip].country})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-xs text-muted-foreground">Aucune connexion trouvée.</p>}
                </div>

                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2"><Eye className="h-4 w-4" /> Visites ({traceResult.visits?.length || 0})</h3>
                  {traceResult.visits?.length > 0 ? (
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {traceResult.visits.map((v, i) => (
                        <div key={i} className="text-xs text-muted-foreground">{v.url} — {formatDate(v.created_at)}</div>
                      ))}
                    </div>
                  ) : <p className="text-xs text-muted-foreground">Aucune visite reliée trouvée (les visites anonymes ne sont pas toujours rattachées à un email).</p>}
                </div>

                {traceResult.type === 'email' && (
                  <>
                    <div>
                      <h3 className="font-medium mb-2 flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Messages envoyés ({traceResult.messages_sent?.length || 0})</h3>
                      {traceResult.messages_sent?.length > 0 ? (
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {traceResult.messages_sent.map((m, i) => (
                            <div key={i} className="text-xs text-muted-foreground">{m.type === 'voice' ? '🎤 Message vocal' : m.content} — {formatDate(m.created_at)}</div>
                          ))}
                        </div>
                      ) : <p className="text-xs text-muted-foreground">Aucun message trouvé.</p>}
                    </div>

                    <div>
                      <h3 className="font-medium mb-2 flex items-center gap-2"><Flag className="h-4 w-4" /> Signalements ({(traceResult.reports_against?.length || 0) + (traceResult.reports_filed?.length || 0)})</h3>
                      {traceResult.reports_against?.length > 0 && (
                        <p className="text-xs text-destructive">⚠️ {traceResult.reports_against.length} signalement(s) reçu(s) contre cette personne</p>
                      )}
                      {traceResult.reports_filed?.length > 0 && (
                        <p className="text-xs text-muted-foreground">{traceResult.reports_filed.length} signalement(s) déposé(s) par cette personne</p>
                      )}
                      {(!traceResult.reports_against?.length && !traceResult.reports_filed?.length) && (
                        <p className="text-xs text-muted-foreground">Aucun signalement.</p>
                      )}
                    </div>

                    <div>
                      <h3 className="font-medium mb-2 flex items-center gap-2"><ShieldOff className="h-4 w-4" /> Blocages ({traceResult.blocks?.length || 0})</h3>
                      {traceResult.blocks?.length > 0 ? (
                        <div className="space-y-1">
                          {traceResult.blocks.map((b, i) => (
                            <div key={i} className="text-xs text-muted-foreground">{b.blocker_email} a bloqué {b.blocked_email}</div>
                          ))}
                        </div>
                      ) : <p className="text-xs text-muted-foreground">Aucun blocage.</p>}
                    </div>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Filtres */}
        <div className="flex flex-wrap gap-3 mb-4">
          <select
            value={filterKind}
            onChange={(e) => setFilterKind(e.target.value)}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            data-testid="filter-kind"
          >
            <option value="all">Tout (visites + connexions)</option>
            <option value="login">Connexions uniquement</option>
            <option value="visit">Visites uniquement</option>
          </select>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Filtrer par IP" value={searchIp} onChange={(e) => setSearchIp(e.target.value)} className="pl-8 w-44" data-testid="filter-ip" />
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Filtrer par email" value={searchEmail} onChange={(e) => setSearchEmail(e.target.value)} className="pl-8 w-56" data-testid="filter-email" />
          </div>
        </div>

        {/* Tableau */}
        <div className="border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Type</th>
                  <th className="text-left px-4 py-2 font-medium">Email</th>
                  <th className="text-left px-4 py-2 font-medium">Adresse IP</th>
                  <th className="text-left px-4 py-2 font-medium">Navigateur / appareil</th>
                  <th className="text-left px-4 py-2 font-medium">Page</th>
                  <th className="text-left px-4 py-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody data-testid="connections-body">
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Chargement...</td></tr>
                ) : connections.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Aucune donnée pour ces filtres.</td></tr>
                ) : (
                  connections.map((c, idx) => (
                    <tr key={idx} className="border-t border-border/50 hover:bg-muted/30">
                      <td className="px-4 py-2">
                        {c.source === 'login' ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${c.success ? 'bg-secondary/15 text-secondary' : 'bg-destructive/15 text-destructive'}`}>
                            {c.success ? 'Connexion réussie' : 'Connexion échouée'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary/15 text-primary">Visite</span>
                        )}
                      </td>
                      <td className="px-4 py-2">{c.email || '—'}</td>
                      <td className="px-4 py-2 font-mono text-xs">
                        <div className="flex items-center gap-2">
                          <span>{c.ip || '—'}</span>
                          {c.ip && (
                            <button
                              onClick={() => handleGeolocate(c.ip)}
                              className="text-muted-foreground hover:text-primary"
                              title="Localiser cette IP"
                              data-testid={`geo-btn-${idx}`}
                            >
                              <MapPin className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        {geoResults[c.ip] && (
                          <div className="text-[11px] text-muted-foreground mt-1">
                            {geoResults[c.ip].error
                              ? geoResults[c.ip].error
                              : geoResults[c.ip].note
                              ? geoResults[c.ip].note
                              : `${geoResults[c.ip].city || '?'}, ${geoResults[c.ip].country || '?'} — ${geoResults[c.ip].isp || ''}`}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground max-w-[220px] truncate" title={c.user_agent}>{c.user_agent || '—'}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground max-w-[180px] truncate">{c.url || '—'}</td>
                      <td className="px-4 py-2 text-xs whitespace-nowrap">{formatDate(c.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityMonitor;
